import React, { useEffect, useRef, useState } from 'react';
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import {colors as immoColors, createControl, createMap, initMinimalLegend, initMinimalMap, initOutlineCity } from "../../utils/map/mapUtils";
import { risksList, urbanismeList } from '../../utils/staticInfos';
import { mountFormattedOverlay, onlyUniqueByKey } from '../../utils/overlaysUtils';
import { ImmobilierMap } from './ImmobilierMap';

export const MinimalMapWidget = ({summary, outlineCity, address, layer, infosForImmo}) => {
        const mapRef = useRef(undefined);
        const overlaysGroupRef = useRef(undefined);
        const legendRef = useRef(undefined);
        const outlineCityRef = useRef(undefined);
        const [immoItem, setImmoItem] = useState(undefined);

    const formatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0})
    const colors = immoColors(infosForImmo && infosForImmo.codeInsee);

    const getColorSector = (nb) => {
        const thresholdsNb = infosForImmo.codeInsee != '75056' ? 5 : 10;
        const prices = infosForImmo.immoData.estimates.map(e => e.prix_m2);
        const part = (Math.max(...prices) - Math.min(...prices))/ thresholdsNb;
        const thresholds = Array.from({length: thresholdsNb}, (_, i) => Math.min(...prices) + (part*i)).filter(i => nb > i)

        return thresholds.length > 0 ? colors[thresholds.length - 1] : colors[0];
    }

    useEffect(() => {
        if(summary && outlineCity && layer){
            mapRef.current = createMap(layer);
            legendRef.current = createControl('topleft', false, true);
            outlineCityRef.current = initOutlineCity(outlineCity.COM);
            overlaysGroupRef.current = L.layerGroup().addTo(mapRef.current);
            initMinimalMap(mapRef.current, null, outlineCityRef.current, {address: address});

            if(layer != 'immobilier'){
                // add layer (get in static info + values)
                const selectedItem = mountFormattedOverlay([...risksList, ...urbanismeList].flatMap(category => category.items).find(item => item.id == layer), summary);
                const tilesId = selectedItem.tilesId || selectedItem.id;

                if(selectedItem){
                    fetch(`/values/${tilesId}`)
                        .then(res => res.json())
                        .then(values => {
                            const formattedValues = selectedItem.legend ? selectedItem.legend(values) : values.filter(onlyUniqueByKey("label"));
                           initMinimalLegend(legendRef.current, formattedValues, mapRef.current);
                           return formattedValues;
                        })
                        .then(legends => {
                            overlaysGroupRef.current.addLayer(L.vectorGrid.protobuf(`/tiles/${tilesId}/{z}/{x}/{y}.pbf`, {
                                interactive: true,
                                vectorTileLayerStyles: {[tilesId]: (properties) => selectedItem.style(properties, legends)},
                                attribution: selectedItem.attributions ? `© ${selectedItem.attributions}` : null,
                                zIndex: 10
                            })
                            .on('click', (layerElement) => {
                                const content = selectedItem.popupContent(layerElement.layer.properties);
                                if(content){
                                    L.popup()
                                    .setLatLng(layerElement.latlng)
                                    .setContent(content)
                                    .openOn(mapRef.current);
                                }
                            }))
                        })
                }
            } else {
                const communes = infosForImmo.immoData.communes;
                const irisFeatures = infosForImmo.immoData.iris.features;
                irisFeatures.map(feature => {
                    const { properties } = feature;
                    const estimate = infosForImmo.immoData.estimates.find(e => e.num_iris === properties.codeIris)
                    const multipleComs = communes && communes.features && (communes.features.length > 1);
                    const commune = communes.features.find(c => properties.codeIris.startsWith(c.properties.com));
                    const libelle = ((multipleComs && commune && properties.typeIris.code !== 'Z') ? commune.properties.libelle + ' - ' + properties.libelleIris : properties.libelleIris);

                    feature.properties = {
                        ...properties,
                        prix_m2: estimate ? estimate.prix_m2 : undefined,
                        nb_ventes: estimate ? estimate.nb_ventes : undefined,
                        color: estimate ? getColorSector(estimate.prix_m2) : '#000',
                        label: estimate ? `${formatter.format(estimate.prix_m2)} / m²` : undefined,
                        libelleIris: libelle
                    }
                    return feature;
                })

                overlaysGroupRef.current.addLayer(
                    L.geoJSON(irisFeatures, {
                        style: (feature) =>  ({
                            color: feature.properties.color,
                            weight: 1,
                            fillOpacity: 0.7,
                            fill: true,
                        }),
                        onEachFeature: (feature, layer) => layer.bindPopup(feature.properties.libelleIris ? `<p>${feature.properties.libelleIris}<br/>${feature.properties.label}</p>` : feature.properties.label)
                    })
                )

                if(outlineCity.ARM.length > 0){
                    overlaysGroupRef.current.addLayer(L.geoJSON(outlineCity.ARM, {
                        style: {
                            color: "#918F8F",
                            weight: 2,
                            fill: false,
                            dashArray: "4"
                        }
                    }));
                }
                outlineCityRef.current.bringToFront();
            }
        }

    }, [])

    return (
        <div className="w-100 rounded-lg" style={{height: '400px', border: '1px solid #cfcfcf'}}>
            <div
                id={`${layer}-map`}
                style={{ height: '100%'}}
                role="img"
                aria-label={`Carte de la ville`}
                className="position-relative h-100 rounded-lg"
            >
            </div>
        </div>
    )
}